/**
 * Reusable marketing UI blocks — ActanonEdu design system (mk-* tokens).
 */
import { Link } from 'react-router-dom';
import { useId, useState } from 'react';
import { Check, X, ChevronDown, ArrowRight, MessageCircle } from 'lucide-react';
import { BRAND } from '../content';
import { AnimatedCounter } from './PremiumBlocks';

// ─── Buttons (link-based so every CTA is a real destination) ───
const BTN_BASE = 'inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm h-12 px-6 transition-[background-color,color,border-color,transform] duration-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mk-cyan focus-visible:ring-offset-2';
const BTN_VARIANTS = {
  primary: 'bg-mk-blue text-white hover:bg-mk-deep focus-visible:ring-offset-white',
  secondary: 'border border-mk-line bg-white text-mk-ink hover:border-mk-blue hover:text-mk-deep dark:bg-transparent dark:text-white dark:border-mk-edge',
  ghostDark: 'border border-white/20 text-white hover:bg-white/10 focus-visible:ring-offset-mk-midnight',
  light: 'bg-white text-mk-ink hover:bg-mk-ice focus-visible:ring-offset-mk-midnight',
};

export function scrollIfSameHash(to) {
  const [path, id] = (to || '').split('#');
  if (!id || window.location.pathname !== (path || '/')) return;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function CtaLink({ to, href, variant = 'primary', children, className = '', testid, arrow = false, ...rest }) {
  const cls = `${BTN_BASE} ${BTN_VARIANTS[variant]} ${className}`;
  const inner = <>{children}{arrow && <ArrowRight className="h-4 w-4" aria-hidden="true" />}</>;
  if (href) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testid} {...rest}>{inner}</a>;
  }
  return <Link to={to} className={cls} data-testid={testid} onClick={() => scrollIfSameHash(to)} {...rest}>{inner}</Link>;
}

// ─── Eyebrow label ───
export function Eyebrow({ children, dark = false, className = '' }) {
  return (
    <div className={`inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] ${dark ? 'text-mk-cyan' : 'text-mk-blue'} ${className}`}>
      <span className={`h-px w-6 ${dark ? 'bg-mk-cyan' : 'bg-mk-blue'}`} aria-hidden="true" />
      {children}
    </div>
  );
}

// ─── Orbital motif (decorative, behind content) ───
export function OrbitalMotif({ className = '' }) {
  return (
    <div className={`absolute pointer-events-none ${className}`} aria-hidden="true">
      <div className="mk-orbit inset-0" />
      <div className="mk-orbit inset-[12%] border-mk-blue/20" />
      <div className="mk-orbit inset-[26%]" />
      <span className="absolute top-[12%] right-[22%] h-2 w-2 rounded-full bg-mk-cyan/70" />
      <span className="absolute bottom-[26%] left-[14%] h-1.5 w-1.5 rounded-full bg-mk-blue" />
    </div>
  );
}

// ─── Product frame — wraps a real app screenshot ───
export function ProductFrame({ src, alt, label, className = '', eager = false, testid, dark = false }) {
  return (
    <figure className={`rounded-2xl overflow-hidden border ${dark ? 'border-mk-edge bg-mk-navy' : 'border-mk-line bg-white dark:border-mk-edge dark:bg-mk-navy'} shadow-[0_24px_60px_-24px_rgba(7,20,38,0.45)] ${className}`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 px-3.5 h-8 border-b ${dark ? 'border-mk-edge' : 'border-mk-line dark:border-mk-edge'}`}>
        <span className="h-2 w-2 rounded-full bg-mk-line" /><span className="h-2 w-2 rounded-full bg-mk-line" /><span className="h-2 w-2 rounded-full bg-mk-line" />
        {label && <span className={`ml-2 text-[11px] font-medium ${dark ? 'text-slate-400' : 'text-mk-muted'}`}>{label}</span>}
      </div>
      <img src={src} alt={alt} width="1440" height="900" loading={eager ? 'eager' : 'lazy'} decoding="async" className="block w-full h-auto" />
    </figure>
  );
}

// ─── Page hero (top of every sub-page) ───
export function PageHero({ eyebrow, title, accent, subtitle, children, testid }) {
  return (
    <section className="relative overflow-hidden bg-mk-midnight text-white" data-testid={testid}>
      <div className="absolute inset-0 bg-[radial-gradient(60%_80%_at_85%_0%,rgba(8,120,249,0.22),transparent_60%)]" aria-hidden="true" />
      <OrbitalMotif className="-right-40 -top-40 h-[520px] w-[520px] hidden md:block" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {eyebrow && <Eyebrow dark className="mb-5">{eyebrow}</Eyebrow>}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">
          {title}
          {accent && <>{' '}<span className="text-mk-cyan">{accent}</span></>}
        </h1>
        {subtitle && <p className="mt-5 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">{subtitle}</p>}
        {children && <div className="mt-8">{children}</div>}
      </div>
    </section>
  );
}

// ─── Section wrapper ───
export function Section({ children, className = '', tone = 'default', id, ...rest }) {
  const tones = {
    default: 'bg-white dark:bg-mk-midnight',
    muted: 'bg-mk-ice dark:bg-mk-navy/60',
    dark: 'bg-mk-midnight text-white',
  };
  return (
    <section id={id} className={`py-16 sm:py-24 scroll-mt-20 ${tones[tone]} ${className}`} {...rest}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

// ─── Section header ───
export function SectionHeader({ eyebrow, title, subtitle, align = 'left', dark = false }) {
  const alignCls = align === 'center' ? 'text-center mx-auto' : 'text-left';
  return (
    <div className={`max-w-3xl mb-10 sm:mb-14 ${alignCls}`}>
      {eyebrow && <Eyebrow dark={dark} className="mb-4">{eyebrow}</Eyebrow>}
      <h2 className={`text-3xl sm:text-4xl font-bold tracking-tight leading-tight ${dark ? 'text-white' : 'text-mk-ink dark:text-white'}`}>{title}</h2>
      {subtitle && <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? 'text-slate-300' : 'text-mk-muted dark:text-slate-300'}`}>{subtitle}</p>}
    </div>
  );
}

// ─── Feature card ───
export function FeatureCard({ icon: Icon, name, summary, bullets = [], testid }) {
  return (
    <div
      className="group rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy p-6 transition-[border-color,box-shadow,transform] duration-200 hover:border-mk-blue/50 hover:shadow-[0_16px_40px_-20px_rgba(8,120,249,0.35)] hover:-translate-y-0.5"
      data-testid={testid}
    >
      <div className="h-11 w-11 rounded-xl grid place-items-center mb-5 bg-mk-ice dark:bg-mk-midnight text-mk-blue border border-mk-line dark:border-mk-edge">
        {Icon ? <Icon className="h-5 w-5" aria-hidden="true" /> : null}
      </div>
      <h3 className="text-lg font-bold tracking-tight text-mk-ink dark:text-white">{name}</h3>
      {summary && <p className="mt-2 text-sm text-mk-muted dark:text-slate-300 leading-relaxed">{summary}</p>}
      {bullets.length > 0 && (
        <ul className="mt-4 space-y-2">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[13px] text-mk-ink/80 dark:text-slate-300 leading-snug">
              <Check className="h-3.5 w-3.5 mt-0.5 text-mk-blue shrink-0" aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Stat strip ───
export function StatStrip({ items }) {
  const cols = items.length >= 6 ? 'md:grid-cols-3 lg:grid-cols-6' : items.length >= 4 ? 'md:grid-cols-4' : 'md:grid-cols-3';
  return (
    <div className={`grid grid-cols-2 ${cols} gap-6 py-10 rounded-2xl bg-mk-midnight text-white px-6 sm:px-8 my-10 border border-mk-edge`} data-testid="stat-strip">
      {items.map((it, i) => (
        <div key={i}>
          <div className="text-3xl sm:text-4xl font-extrabold tracking-tight text-mk-cyan font-heading">
            {typeof it.to === 'number' ? <AnimatedCounter to={it.to} prefix={it.prefix} suffix={it.suffix} /> : it.value}
          </div>
          <div className="text-xs sm:text-sm text-slate-300 mt-1">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── CTA strip (bottom of most pages) ───
export function CTAStrip({ title, subtitle, primary, secondary, testid = 'cta-strip' }) {
  return (
    <Section>
      <div className="relative overflow-hidden rounded-3xl bg-mk-midnight text-white p-8 sm:p-14 border border-mk-edge" data-testid={testid}>
        <div className="absolute inset-0 bg-[radial-gradient(50%_90%_at_100%_100%,rgba(32,217,242,0.16),transparent_60%)]" aria-hidden="true" />
        <OrbitalMotif className="-right-24 -bottom-32 h-80 w-80 hidden sm:block" />
        <div className="relative max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{title}</h2>
          {subtitle && <p className="mt-4 text-slate-300 text-base sm:text-lg">{subtitle}</p>}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            {primary && (
              primary.href?.startsWith('http')
                ? <CtaLink href={primary.href} variant="primary" arrow testid={`${testid}-primary`}>{primary.label}</CtaLink>
                : <CtaLink to={primary.href} variant="primary" arrow testid={`${testid}-primary`}>{primary.label}</CtaLink>
            )}
            {secondary && (
              <CtaLink href={BRAND.whatsappLink(secondary.wa || 'Hi! I came from the ActanonEdu website.')} variant="ghostDark" testid={`${testid}-secondary`}>
                <MessageCircle className="h-4 w-4" aria-hidden="true" /> {secondary.label}
              </CtaLink>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── FAQ accordion (accessible: button + region) ───
export function FAQAccordion({ items, testid = 'faq' }) {
  const [open, setOpen] = useState(0);
  const uid = useId();
  return (
    <div className="divide-y divide-mk-line dark:divide-mk-edge rounded-2xl border border-mk-line dark:border-mk-edge bg-white dark:bg-mk-navy overflow-hidden" data-testid={testid}>
      {items.map((it, i) => {
        const isOpen = open === i;
        const btnId = `${uid}-q-${i}`;
        const panelId = `${uid}-a-${i}`;
        return (
          <div key={i}>
            <h3 className="m-0">
              <button
                id={btnId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 sm:px-6 py-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mk-blue text-mk-ink dark:text-white hover:bg-mk-ice/70 dark:hover:bg-mk-midnight/60 transition-colors"
                data-testid={`${testid}-q-${i}`}
              >
                <span className="font-semibold text-sm sm:text-base font-heading">{it.q}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 transition-transform duration-200 text-mk-blue ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
            </h3>
            {isOpen && (
              <div id={panelId} role="region" aria-labelledby={btnId} className="px-5 sm:px-6 pb-5 text-sm sm:text-[15px] text-mk-muted dark:text-slate-300 leading-relaxed" data-testid={`${testid}-a-${i}`}>
                {it.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Comparison table ───
export function ComparisonTable({ data, testid = 'comparison' }) {
  const last = data.columns.length - 1;
  return (
    <div className="overflow-x-auto rounded-2xl border border-mk-line dark:border-mk-edge" data-testid={testid}>
      <table className="min-w-[640px] w-full text-sm">
        <thead className="bg-mk-ice dark:bg-mk-navy">
          <tr>
            <th scope="col" className="text-left px-5 py-4 font-semibold text-mk-muted text-xs uppercase tracking-wider">Capability</th>
            {data.columns.map((c, i) => (
              <th scope="col" key={c} className={`text-center px-5 py-4 font-semibold text-xs uppercase tracking-wider ${i === last ? 'text-mk-deep dark:text-mk-cyan bg-mk-blue/10' : 'text-mk-muted'}`}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-mk-midnight divide-y divide-mk-line dark:divide-mk-edge">
          {data.rows.map((row, ri) => (
            <tr key={ri}>
              <th scope="row" className="text-left px-5 py-3.5 font-medium text-mk-ink dark:text-slate-200">{row.label}</th>
              {row.values.map((v, vi) => (
                <td key={vi} className={`px-5 py-3.5 text-center ${vi === row.values.length - 1 ? 'bg-mk-blue/5' : ''}`}>
                  {typeof v === 'boolean'
                    ? (v ? <Check className="inline h-4 w-4 text-mk-blue" aria-label="Yes" /> : <X className="inline h-4 w-4 text-slate-400" aria-label="No" />)
                    : <span className="text-sm font-semibold text-mk-ink dark:text-white">{v}</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Board / label strip ───
export function LogoStrip({ labels = [], title = 'Built for' }) {
  return (
    <div className="border-y border-mk-line dark:border-mk-edge bg-mk-ice dark:bg-mk-navy/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center sm:justify-between gap-4 text-sm">
        <span className="uppercase tracking-[0.2em] text-[11px] font-bold text-mk-muted">{title}</span>
        {labels.map((l) => <span key={l} className="font-semibold text-mk-ink dark:text-slate-200">{l}</span>)}
      </div>
    </div>
  );
}
